/*
 * @Copyright: Copyright (c) Pray LLC. All rights reserved.
 * @Author: Raguvind Tharanitharan
 * @Date: 2022-09-12 10:18:12
 * @Last Modified by: Raguvind Tharanitharan
 * @Last Modified time: 2022-09-12 22:12:00
*/

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Snackbar, Alert, AlertTitle, Typography, Divider, Link } from '@mui/material';
import { AccountCircleRounded, Google } from '@mui/icons-material';
import { useGoogleLogin } from '@react-oauth/google';
import { authenticateUser, updateUserDetails } from '../../common/stores/user';
import { PrayButton, PrayView } from '../../common';
import EventService from '../../common/services/events-service';
import withRouter from '../../with-router';
import logo from '../resources/logo.svg';
import '../styles/local-style.css';

export default withRouter(function LoginView(props) {
  const [error, seterror] = useState(false);
  const [loading, setloading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * 
   * @param {*} tokenResponse 
   */
  const onSuccess = async (tokenResponse) => {
    setloading(true);
    dispatch(authenticateUser(tokenResponse.code))
      .unwrap()
      .then(data => {
        EventService.logEvent('User Logged In', { view_name: 'Login' }, {email: data.email});
        dispatch(updateUserDetails(data));
        navigate('/dashboard');
      });
  };

  const onFailure = (err) => {
    EventService.logEvent('Error', { view_name: 'Login', error_description: err.toString() });
    seterror(true);
    console.log('failed:', err);
  };

  const handleClose = () => {
    seterror(false);
  };

  const login = useGoogleLogin({
    onSuccess: onSuccess,//tokenResponse => console.log(tokenResponse), testing
    onError: onFailure,
    onFailure: onFailure,
    flow: 'auth-code',
  });

  return <>
    <PrayView viewName='Login Screen'>
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }} className="login-container">
        <img src={logo} className="app-logo" alt="logo" />
        <Typography variant='h3' sx={{ m: 2 }}>Make Prayer a Priority</Typography>
        <Paper variant="outlined" sx={{ mt: 7, mx: 14 }}>
          <AccountCircleRounded sx={{ fontSize: 120, py: 4, color: 'primary.light' }} />
          <Typography variant='subtitle2' sx={{ mx: 4, mb: 2, color: 'secondary.dark' }}>Login with your gmail account to access the dashboard</Typography>
          <Divider sx={{ my: 3, mx: 6, mb: 2 }} variant="middle" />
          <Container sx={{ flex: 1, pb: 3 }} className="login-button">
            <PrayButton
              variant="outlined"
              viewName='Login Screen'
              disabled={loading}
              className="login-with-google-btn"
              startIcon={<Google sx={{ pr: 0.8, borderRight: '1px solid #00000024' }} />}
              onClick={login}
              text='Sign in with Google'
            >
              Sign in with Google
            </PrayButton>
          </Container>
        </Paper>
        <div className='login-footer'>
          <PrayButton text="Privacy Policy" viewName='Login Screen'>
            <Link href='https://www.pray.com/privacy-policy' target="_blank" rel="noreferrer">Privacy Policy</Link>
          </PrayButton>
          <strong>|</strong>
          <PrayButton text="Terms of Service" viewName='Login Screen'>
            <Link href='https://www.pray.com/terms-of-service' target="_blank" rel="noreferrer">Terms of Service</Link>
          </PrayButton>
        </div>
      </Container>
    </PrayView>
    <Snackbar open={error} autoHideDuration={6000} onClose={handleClose}>
      <Alert variant="filled" onClose={handleClose} severity="error" sx={{ width: '100%', textAlign: "left" }}>
        <AlertTitle>Error</AlertTitle>
        Unable to login to the application — <strong>Please contact support for more details!</strong>
      </Alert>
    </Snackbar>
  </>;
});